// mds-dojo — dojo/parser. Walks the DOM for data-dojo-type, builds params from
// attributes (+ data-dojo-props + attach-point/event), and `new ctor(params, node)`.
//
// The AMD loader's job of resolving a type-id to a constructor is replaced by an
// explicit registry the app fills via import.meta.glob (see Typescript/main.ts):
// parser.register(id, ctor). Because every module is preloaded, parse() is fully
// synchronous; it returns an array augmented with a synchronous .then() so
// _WidgetsInTemplateMixin (which does parser.parse(node).then(...)) works.

import JSON5 from 'json5';
import lang from './_base/lang';
import * as array from './_base/array';
import { declaredTypes, registerDeclaredType } from './_base/declare';

interface ParseOptions {
  noStart?: boolean;
  template?: boolean;
  propsThis?: any;
  scope?: string;
  rootNode?: Node;
  inherited?: any;
  defaults?: any;
}

const typeRegistry: Record<string, any> = {};

/**
 * **mds-dojo** — register a constructor under a dojo module id so a
 * `data-dojo-type="<id>"` resolves at parse time. The app's `import.meta.glob` boot
 * (see `Typescript/main.ts`) calls this for every widget — there is no AMD loader to
 * resolve ids, so this registry replaces it.
 *
 * Two mds-specific extras vanilla dojo's parser had no need for:
 *  - if a (clean-shape) widget ships no `declaredClass`, one is DERIVED from the id
 *    basename (build-reliable, unlike terser-mangled `Ctor.name`) — but never for
 *    `dojo/dijit/dojox` core ids (that would clobber globals like `Promise`);
 *  - the dotted `declaredClass` is indexed too, so both `more/Foo` and `more.Foo`
 *    spellings of a `data-dojo-type` resolve.
 *
 * NOT part of vanilla Dojo's parser API.
 * @param id the dojo module id (from the glob's build-time path), e.g. `"Playground/Counter"`.
 * @param ctor the widget constructor (typically an {@link mdsWidget}-wrapped class).
 */
function register(id: string, ctor: any): void {
  typeRegistry[id] = ctor;
  // declaredClass: clean-shape hand-authored widgets ship NO declaredClass (their
  // Object.assign default block is gone). Derive it from the module-id basename so
  // `widget.declaredClass` is a BUILD-RELIABLE string — the id comes from the glob's
  // build-time path, unlike Ctor.name which terser mangles in the prod bundle. Faithful
  // declare() widgets already carry a (dotted) declaredClass, so this only fills the gap.
  // Never derive one for mds-dojo CORE ids (dojo/dijit/dojox): those are not app
  // widgets, and deriving a bare basename here would mirror it onto the global object
  // path below — e.g. `dojo/promise/Promise` → declaredClass 'Promise' → clobbers the
  // native global `Promise` (which then has no `.all`). Faithful dijit/dojox WIDGETS
  // already carry their own dotted declaredClass, so they're unaffected either way.
  // hasOwnProperty, NOT a plain read: every clean app widget inherits its base's
  // declaredClass ('more._WidgetBaseLite') down the chain, so a plain read is always a
  // string and would never fill the leaf's OWN name.
  if (ctor && ctor.prototype
      && !/^(?:dojo|dijit|dojox)\//.test(id)
      && !Object.prototype.hasOwnProperty.call(ctor.prototype, 'declaredClass')) {
    const base = id.substring(id.lastIndexOf('/') + 1);
    if (base) { ctor.prototype.declaredClass = base; }
  }
  // Index the declaredClass too: real-dojo parsers resolve DOTTED type names
  // ("more.HintBox", "WidgetCollection.JDM.…") via the declared name / global
  // object path. Registering it here makes both spellings resolve.
  const declared = ctor && ctor.prototype && ctor.prototype.declaredClass;
  if (typeof declared === 'string' && declared && !(declared in declaredTypes)) {
    registerDeclaredType(declared, ctor);
  }
}

function resolveOne(type: string): any {
  return typeRegistry[type]
    // classic declare("dotted.name", …) + @declare("dotted.name", …) registrations
    || declaredTypes[type]
    // dotted spelling of a module id: "more.Legacy.FileTransfer.Uploader" ⇒ module
    // "more/Legacy/FileTransfer/Uploader" (dot and slash forms are used
    // interchangeably in the views)
    || (type.indexOf('.') !== -1 ? typeRegistry[type.replace(/\./g, '/')] : null)
    || null;
}

function getCtor(types: string[]): any {
  const ctor = resolveOne(types[0]);
  if (!ctor) { return null; }
  if (types.length > 1) {
    // data-dojo-mixins — not used by the oracle; warn and use the primary type.
    console.warn('[mds-dojo/parser] data-dojo-mixins not supported, ignoring extras:', types.slice(1).join(','));
  }
  return ctor;
}

function makeSyncThenable(arr: any[]): any {
  (arr as any).then = function (cb: (a: any[]) => any) {
    const r = cb(arr);
    return makeSyncThenable(Array.isArray(r) ? r : arr);
  };
  return arr;
}

function parseProps(extra: string): any {
  // data-dojo-props is an object-literal body (unquoted keys, single-quoted strings,
  // trailing commas) — JSON5, NOT JSON, and NOT arbitrary JS. Parse it with JSON5 (no
  // eval / new Function, so the bundle is CSP-'unsafe-eval'-free). `${…}` template
  // substitution + Razor rendering have already reduced every value to a literal by the
  // time the parser sees it (verified: the whole codebase's data-dojo-props are literal).
  // A body that isn't valid JSON5 throws — surfacing the bug rather than silently eval'ing.
  return JSON5.parse('{' + extra + '}');
}

function nameMap(ctor: any): Record<string, string> {
  if (ctor._nameCaseMap) { return ctor._nameCaseMap; }
  const map: Record<string, string> = {};
  const proto = ctor.prototype;
  for (const name in proto) {
    if (name.charAt(0) === '_') { continue; }
    map[name.toLowerCase()] = name;
  }
  ctor._nameCaseMap = map;
  return map;
}

function construct(ctor: any, node: any, mixin: any, options: ParseOptions, hmrKey?: string): any {
  const proto = ctor.prototype;
  const params: any = {};
  if (options.defaults) { lang.mixin(params, options.defaults); }
  if (options.inherited) { lang.mixin(params, options.inherited); }

  let extra: string | undefined;
  let jsname: string | undefined;
  const attrs = node.attributes;
  for (let i = 0; i < attrs.length; i++) {
    const item = attrs[i];
    const lcName = item.name.toLowerCase();
    let name = item.name;
    const value = item.value;
    switch (lcName) {
      case 'data-dojo-type':
      case 'dojotype':
      case 'data-dojo-mixins':
        break;
      case 'data-dojo-props':
        extra = value;
        break;
      case 'data-dojo-id':
      case 'jsid':
        jsname = value;
        break;
      case 'data-dojo-attach-point':
      case 'dojoattachpoint':
        params.dojoAttachPoint = value;
        break;
      case 'data-dojo-attach-event':
      case 'dojoattachevent':
        params.dojoAttachEvent = value;
        break;
      case 'class':
        params['class'] = node.className;
        break;
      case 'style':
        params['style'] = node.style && node.style.cssText;
        break;
      default: {
        if (!(name in proto)) {
          const map = nameMap(ctor);
          name = map[lcName] || name;
        }
        if (name in proto) {
          const pv = proto[name];
          switch (typeof pv) {
            case 'string': params[name] = value; break;
            case 'number': params[name] = value.length ? Number(value) : NaN; break;
            case 'boolean': params[name] = value.toLowerCase() !== 'false'; break;
            case 'function':
              // A function-typed prop set via a plain attribute: resolve the value as a
              // dotted global path (dojo semantics), NOT as an eval'd JS body. Inline-JS
              // handler attributes are not used in this codebase (handlers go through
              // data-dojo-attach-event, resolved by method name) — so no new Function.
              params[name] = value ? lang.getObject(value, false) : undefined;
              if (value && params[name] === undefined) {
                console.warn('[mds-dojo/parser] function attr "' + name + '"="' + value + '" did not resolve to a global; inline-JS attributes are not supported (use data-dojo-attach-event).');
              }
              break;
            default:
              if (pv && typeof pv === 'object' && 'length' in pv) {
                params[name] = value ? value.split(/\s*,\s*/) : [];
              } else if (pv instanceof Date) {
                params[name] = value === '' ? new Date('') : (value === 'now' ? new Date() : new Date(value));
              } else {
                try { params[name] = parseProps(name + ':' + value)[name]; } catch (e) { params[name] = value; }
              }
          }
        } else {
          params[name] = value;
        }
      }
    }
  }

  if (extra) {
    try { lang.mixin(params, parseProps(extra)); } catch (e: any) {
      throw new Error(e.toString() + " in data-dojo-props='" + extra + "'");
    }
  }
  if (mixin) { lang.mixin(params, mixin); }

  const instance = new ctor(params, node);
  if (import.meta.env.DEV && hmrKey && instance && instance.domNode) {
    // DEV-ONLY: stamp the instantiated child-widget root with a stable HMR alignment key so the
    // 3-way template merge can pair a pristine template DECL (data-dojo-type + attach-point) with
    // this live root (widgetId, fresh tag from the child's own template). Derived in scan() on the
    // pristine template; MUST stay in lock-step with MERGE_CFG.keyOf in dojo/_base/hmr-runtime.ts.
    // `import.meta.env.DEV` is false in `vite build` → terser drops this branch (parser IS bundled).
    instance.domNode.setAttribute('data-mds-hmr-key', hmrKey);
  }
  if (jsname) { lang.setObject(jsname, instance); }
  return instance;
}

function scan(root: Node, options: ParseOptions): Array<{ node: any; ctor: any; types: string[]; hmrKey?: string }> {
  const list: Array<{ node: any; ctor: any; types: string[]; hmrKey?: string }> = [];

  function walk(node: any, stopped: boolean) {
    // DEV-ONLY per-parent ordinal counter for the widget HMR alignment key (see construct()).
    const hmrOrd: Map<string, number> | null = import.meta.env.DEV ? new Map<string, number>() : null;
    for (let child = node.firstChild; child; child = child.nextSibling) {
      if (child.nodeType !== 1) { continue; }
      const type = child.getAttribute('data-dojo-type') || child.getAttribute('dojoType');
      if (type && !stopped) {
        const mixinsValue = child.getAttribute('data-dojo-mixins');
        const types = mixinsValue ? [type].concat(mixinsValue.split(/\s*,\s*/)) : [type];
        const ctor = getCtor(types);
        let hmrKey: string | undefined;
        if (hmrOrd) {
          // Discriminator = attach-point → author id → type + '#' + ordinal-among-same-type siblings.
          // MUST equal MERGE_CFG.keyOf's pristine-decl derivation in dojo/_base/hmr-runtime.ts.
          const ap = (child.getAttribute('data-dojo-attach-point') || child.getAttribute('dojoAttachPoint') || '').split(/\s*,\s*/)[0];
          const id = child.getAttribute('id');
          const ord = hmrOrd.get(type) || 0;
          hmrOrd.set(type, ord + 1);
          hmrKey = ap ? 'ap:' + ap : id ? 'id:' + id : 'dt:' + type + '#' + ord;
        }
        // DEV carries the alignment key on the entry; prod folds to the original 3-field entry
        // (the whole hmrOrd/hmrKey block above then becomes unused and is DCE'd — zero prod residue).
        list.push(import.meta.env.DEV ? { node: child, ctor, types, hmrKey } : { node: child, ctor, types });
        // Recurse into children unless the widget stops the parser (and we're not
        // in template mode, which forces parsing through stopParser widgets).
        const proto = ctor && ctor.prototype;
        const childStop = !!(proto && proto.stopParser && !options.template) || !!child.stopParser;
        walk(child, childStop);
      } else {
        walk(child, stopped);
      }
    }
  }

  walk(root, false);
  return list;
}

function instantiate(list: Array<{ node: any; ctor: any; types: string[]; hmrKey?: string }>, mixin: any, options: ParseOptions): any[] {
  // Tolerate an unresolvable data-dojo-type: WARN + skip that node instead of throwing —
  // a throw here aborts the whole parse and blanks the page. Real dojo's parser likewise
  // logged + continued past a widget whose module/declared-name it couldn't load, so a STALE
  // ref (a widget renamed/moved without its template being updated, e.g. `more.MultiSelectBox`
  // after the js2ts move to JDM) degrades to "that one widget is missing" rather than "the page
  // is dead". Faithful to dojo — nothing more, nothing less.
  const instances: any[] = [];
  for (const obj of list) {
    const ctor = obj.ctor || getCtor(obj.types);
    if (!ctor) {
      console.warn("[mds-dojo/parser] unresolved data-dojo-type '" + obj.types.join()
        + "' — skipping node (stale or removed widget ref); the rest of the page still parses.");
      continue;
    }
    // DEV threads the HMR alignment key to construct(); prod folds to the original 4-arg call.
    instances.push(import.meta.env.DEV
      ? construct(ctor, obj.node, mixin, options, obj.hmrKey)
      : construct(ctor, obj.node, mixin, options));
  }

  if (!(mixin && mixin._started) && !options.noStart) {
    array.forEach(instances, (inst: any) => {
      if (inst && typeof inst.startup === 'function' && !inst._started) { inst.startup(); }
    });
  }
  return instances;
}

function parse(rootNode?: any, options?: ParseOptions): any {
  if (rootNode && typeof rootNode !== 'string' && !('nodeType' in rootNode)) {
    options = rootNode;
    rootNode = (options as ParseOptions).rootNode;
  }
  const root = rootNode ? (typeof rootNode === 'string' ? document.getElementById(rootNode) : rootNode) : document.body;
  options = options || {};
  const mixin = options.template ? { template: true } : {};

  const list = scan(root, options);
  const instances = instantiate(list, mixin, options);
  return makeSyncThenable(instances.slice());
}

function instantiateNodes(nodes: any[], mixin?: any, options?: ParseOptions): any[] {
  options = options || {};
  const list: Array<{ node: any; ctor: any; types: string[] }> = [];
  array.forEach(nodes, (node: any) => {
    const type = (mixin && mixin.dojoType) || node.getAttribute('data-dojo-type') || node.getAttribute('dojoType');
    if (type) {
      const mixinsValue = node.getAttribute('data-dojo-mixins');
      const types = mixinsValue ? [type].concat(mixinsValue.split(/\s*,\s*/)) : [type];
      list.push({ node, ctor: getCtor(types), types });
    }
  });
  return instantiate(list, mixin || {}, options);
}

const parser = {
  register,
  parse,
  instantiate: instantiateNodes,
  scan,
  construct,
  _registry: typeRegistry,
};
export default parser;
